import classNames from 'classnames';
import type { LucideIcon } from 'lucide-react';
import {
  CircleHelp,
  FileText,
  MessageCircle,
  Sparkles
} from 'lucide-react';
import SubtleBadge from '@/components/common/SubtleBadge';
import {
  getThreadTitlePresentation,
  isLikelyUrl,
  parseThreadContextSegments,
  type ThreadTitleKind
} from '../utils/threadHistoryDisplay';

export function threadHistoryIconForKind(
  kind: ThreadTitleKind
): LucideIcon {
  switch (kind) {
    case 'skill':
      return Sparkles;
    case 'prompt':
      return FileText;
    case 'suggestion':
      return CircleHelp;
    default:
      return MessageCircle;
  }
}

type McpThreadHistoryPresentationProps = {
  title: string | null | undefined;
  layout: 'sidebar' | 'page';
};

function badgeBg(kind: ThreadTitleKind): 'success' | 'info' | 'primary' {
  if (kind === 'skill') return 'success';
  if (kind === 'suggestion') return 'primary';
  return 'info';
}

function badgeLabel(kind: ThreadTitleKind): string {
  if (kind === 'skill') return 'مهارت';
  if (kind === 'suggestion') return 'سوالات متداول';
  return 'قالب';
}

function McpThreadHistoryContextLine({
  context,
  layout
}: {
  context: string;
  layout: 'sidebar' | 'page';
}) {
  const segments = parseThreadContextSegments(context);
  if (segments.length === 0) return null;

  const isSidebar = layout === 'sidebar';
  const titleAttr = segments
    .map(segment =>
      segment.label
        ? `${segment.label}: ${segment.value}`
        : segment.value
    )
    .join(' — ');

  return (
    <span
      className={classNames(
        'text-600 d-block min-w-0',
        isSidebar ? 'fs--2 text-truncate' : 'fs--1 text-break'
      )}
      title={titleAttr}
    >
      {segments.map((segment, index) => (
        <span key={`${segment.label ?? 'v'}-${index}`}>
          {index > 0 ? <span aria-hidden> — </span> : null}
          {segment.label ? (
            <>
              <span className="text-500">{segment.label}:</span>{' '}
              {isLikelyUrl(segment.value) ? (
                <span
                  dir="ltr"
                  className="d-inline-block [unicode-bidi:isolate]"
                >
                  {segment.value}
                </span>
              ) : (
                segment.value
              )}
            </>
          ) : isLikelyUrl(segment.value) ? (
            <span dir="ltr" className="d-inline-block [unicode-bidi:isolate]">
              {segment.value}
            </span>
          ) : (
            segment.value
          )}
        </span>
      ))}
    </span>
  );
}

function iconToneClass(kind: ThreadTitleKind): string {
  switch (kind) {
    case 'skill':
      return 'mcp-thread-history-icon--skill';
    case 'prompt':
      return 'mcp-thread-history-icon--prompt';
    case 'suggestion':
      return 'mcp-thread-history-icon--suggestion';
    default:
      return 'mcp-thread-history-icon--chat';
  }
}

export function McpThreadHistoryPresentation({
  title,
  layout
}: McpThreadHistoryPresentationProps) {
  const pres = getThreadTitlePresentation(title);
  const isSidebar = layout === 'sidebar';
  const showKindBadge =
    pres.kind === 'skill' ||
    pres.kind === 'prompt' ||
    pres.kind === 'suggestion';

  return (
    <span className="d-flex flex-column gap-1 min-w-0 flex-grow-1">
      <span
        className="d-inline-flex align-items-center gap-2 min-w-0"
        dir="rtl"
      >
        <span
          className={classNames(
            'fw-semi-bold min-w-0 fs--1',
            isSidebar ? 'mcp-history-item-title' : 'text-break d-block'
          )}
          style={isSidebar ? undefined : { whiteSpace: 'pre-wrap' }}
        >
          {pres.label}
        </span>
        {showKindBadge ? (
          <SubtleBadge
            bg={badgeBg(pres.kind)}
            pill
            className="fs--2 flex-shrink-0 mb-0"
          >
            {badgeLabel(pres.kind)}
          </SubtleBadge>
        ) : null}
      </span>
      {pres.context ? (
        <McpThreadHistoryContextLine
          context={pres.context}
          layout={layout}
        />
      ) : null}
    </span>
  );
}

type McpThreadHistoryIconProps = {
  title: string | null | undefined;
  size?: number;
  className?: string;
};

export function McpThreadHistoryIcon({
  title,
  size = 16,
  className
}: McpThreadHistoryIconProps) {
  const pres = getThreadTitlePresentation(title);
  const kind = pres.kind;
  const Icon = threadHistoryIconForKind(kind);
  return (
    <span
      className={classNames(
        'mcp-thread-history-icon',
        iconToneClass(kind),
        className
      )}
    >
      <Icon size={size} strokeWidth={2} aria-hidden />
    </span>
  );
}
